import { useState, useEffect, useMemo, useContext } from "react";
import toastError from "../../errors/toastError";
import { format, sub } from "date-fns";
import api from "../../services/api";
import useDebounce from "../useDebounce";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTickets } from "../../services/ticketsApi";

const useTickets = ({
  searchParam,
  tags,
  users,
  flowIds,
  pageNumber,
  status,
  date,
  dateStart,
  dateEnd,
  updatedAt,
  showAll,
  queueIds,
  withUnreadMessages,
  whatsappIds,
  statusFilter,
  forceSearch,
  userFilter,
  sortTickets,
  searchOnMessages,
  contactTypes,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const { user } = useContext(AuthContext);

  const debouncedSearchParam = useDebounce(searchParam, 500);

  useEffect(() => {
    setTickets([]);
  }, [
    debouncedSearchParam,
    tags,
    users,
    flowIds,
    status,
    date,
    dateStart,
    dateEnd,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    contactTypes,
    user?.allowGroup,
    user?.allHistoric,
    user?.allTicket,
    user?.allUserChat,
    user?.allowViewAllClosedTickets,
    user?.profile,
  ]);

  useEffect(() => {
    const handleTicketsChanged = () => {
      setRefreshTick((prev) => prev + 1);
    };

    window.addEventListener("tickets:changed", handleTicketsChanged);
    return () => {
      window.removeEventListener("tickets:changed", handleTicketsChanged);
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    const fetchTickets = async () => {
      if (userFilter === undefined || userFilter === null) {
        try {
          const requestShowAll = Boolean(showAll);
          const { data } = await getTickets({
            params: {
              searchParam: debouncedSearchParam,
              pageNumber,
              tags,
              users,
              flowIds,
              status,
              date,
              dateStart,
              dateEnd,
              updatedAt,
              showAll: requestShowAll ? "true" : "false",
              queueIds,
              withUnreadMessages,
              whatsapps: whatsappIds,
              statusFilter,
              sortTickets,
              searchOnMessages,
              contactTypes,
            },
          });

          if (!isActive) return;

          if (!data || !Array.isArray(data.tickets)) {
            setLoading(false);
            return;
          }

          const postProcess = (arr) => {
            const normalizedProfile = String(user?.profile || "").toLowerCase();
            const isPrivileged = ["admin", "super"].includes(normalizedProfile);
            const canViewOtherUsers =
              isPrivileged || user?.allUserChat === "enabled";

            // A API é a autoridade, mas a lista também deve falhar fechado.
            // Pendentes continuam compartilhados pela fila; conversas abertas,
            // fechadas e resultados de busca exigem dono/participante.
            if (
              !showAll &&
              !canViewOtherUsers &&
              ["open", "closed", "search"].includes(status)
            ) {
              const canViewAllClosed =
                status === "closed" &&
                user?.allowViewAllClosedTickets === "enabled";

              if (!canViewAllClosed) {
                return arr.filter((ticket) => {
                  if (!ticket) return false;
                  const isOwner = Number(ticket.userId) === Number(user?.id);
                  const isParticipant = (ticket.participants || []).some(
                    (participant) => Number(participant?.id) === Number(user?.id)
                  );
                  return isOwner || isParticipant;
                });
              }
            }

            if (status === "closed" && user?.allowGroup === true && !showAll) {
              return arr.filter(
                (t) => t && (t.isGroup === true || t.userId === user?.id)
              );
            }
            return arr;
          };

          setTickets((prevTickets) => {
            if (pageNumber === 1) {
              return postProcess(data.tickets);
            }
            const prevIds = new Set(prevTickets.map((t) => t && t.id));
            const toAppend = data.tickets.filter(
              (t) => t && t.id && !prevIds.has(t.id)
            );
            return postProcess([...prevTickets, ...toAppend]);
          });

          setHasMore(data.hasMore);
          setCount(data.count);
          setLoading(false);
        } catch (err) {
          if (!isActive) return;
          setLoading(false);
          toastError(err);
        }
      } else {
        try {
          const { data } = await api.get("/dashboard/moments", {
            params: {
              status,
              showAll,
              queueIds,
              dateStart: format(sub(new Date(), { days: 30 }), "yyyy-MM-dd"),
              dateEnd: format(new Date(), "yyyy-MM-dd"),
              userId: userFilter,
            },
          });

          if (!isActive) return;

          let filteredTickets = data.filter(
            (item) => item.userId === userFilter
          );
          setTickets(filteredTickets);
          setHasMore(null);
          setLoading(false);
        } catch (err) {
          if (!isActive) return;
          setLoading(false);
          toastError(err);
        }
      }
    };
    fetchTickets();

    return () => {
      isActive = false;
    };
  }, [
    debouncedSearchParam,
    tags,
    users,
    flowIds,
    pageNumber,
    status,
    date,
    dateStart,
    dateEnd,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    contactTypes,
    userFilter,
    user?.allowGroup,
    user?.id,
    user?.allHistoric,
    user?.allTicket,
    user?.allUserChat,
    user?.allowViewAllClosedTickets,
    user?.profile,
    refreshTick,
  ]);

  const processedTickets = useMemo(() => {
    return tickets;
  }, [tickets]);

  return { tickets: processedTickets, loading, hasMore, count };
};

export default useTickets;
